import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Edit2, Trash2, DollarSign, Star, Save, 
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, 
  ArrowUp, ArrowDown, ArrowUpDown, Copy, Check, GripVertical, RefreshCw,
  LayoutGrid, List as ListIcon, Package, Eye
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Product } from '@/types/product';
import { productApi, ProductStats } from '@/services/productApi';
import { categoryApi } from '@/services/categoryApi';
import { formatPrice } from '@/utils/productUtils';
import { ImagePreviewModal } from '@/components/ImagePreviewModal';
import toast from 'react-hot-toast';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
const SEARCH_DEBOUNCE_MS = 400;

type SortField = 'createdAt' | 'basePrice' | 'stock' | 'featuredOrder';
type SortDir = 'asc' | 'desc';

interface Category {
  id: string;
  name: string;
}

interface SortableProductRowProps {
  product: Product;
  index: number;
  totalItems: number;
  isFeaturedMode: boolean;
  selectedIds: Set<string>;
  savingProductIds: Record<string, boolean>;
  priceDrafts: Record<string, string>;
  stockDrafts: Record<string, string>;
  copiedId: string | null;
  getCategoryName: (id: string) => string;
  onSelectOne: (id: string) => void;
  onPreviewImage: (url: string, title: string) => void;
  onCopySlug: (e: React.MouseEvent, slug: string) => void;
  onPriceChange: (id: string, val: string) => void;
  onPriceSave: (p: Product) => void;
  onStockChange: (id: string, val: string) => void;
  onStockSave: (p: Product) => void;
  onFeaturedToggle: (p: Product, checked: boolean) => void;
  onStatusToggle: (p: Product) => void;
  onDelete: (id: string, title: string) => void;
  onNavigateEdit: (id: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onMoveToRank: (fromIndex: number, targetRank: number) => void;
}

const SortableProductRow: React.FC<SortableProductRowProps> = ({
  product,
  index,
  totalItems,
  isFeaturedMode,
  selectedIds,
  savingProductIds,
  priceDrafts,
  stockDrafts,
  copiedId,
  getCategoryName,
  onSelectOne,
  onPreviewImage,
  onCopySlug,
  onPriceChange,
  onPriceSave,
  onStockChange,
  onStockSave,
  onFeaturedToggle,
  onStatusToggle,
  onDelete,
  onNavigateEdit,
  onMoveUp,
  onMoveDown,
  onMoveToRank,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id, disabled: !isFeaturedMode });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    position: 'relative',
    zIndex: isDragging ? 50 : undefined,
  };

  const [rankInput, setRankInput] = useState(String(index + 1));
  useEffect(() => {
    setRankInput(String(index + 1));
  }, [index]);

  const commitRank = () => {
    const parsed = parseInt(rankInput, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= totalItems && parsed !== index + 1) {
      onMoveToRank(index, parsed);
    } else {
      setRankInput(String(index + 1));
    }
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`transition-colors ${
        isDragging
          ? 'bg-amber-50/90 dark:bg-amber-950/40 ring-2 ring-amber-400'
          : 'hover:bg-slate-50/80 dark:hover:bg-slate-800/50'
      }`}
    >
      {/* 1. Drag Handle & Rank or Checkbox */}
      <td className="px-4 py-3">
        {isFeaturedMode ? (
          <div className="flex items-center space-x-1.5">
            <button
              type="button"
              className="p-1 text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 cursor-grab active:cursor-grabbing rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Sürükleyip sıralamayı değiştirin"
              {...attributes}
              {...listeners}
            >
              <GripVertical size={16} />
            </button>
            <div className="flex items-center space-x-1 bg-amber-50 dark:bg-amber-950/60 px-1.5 py-0.5 rounded-lg border border-amber-300/80 shadow-xs">
              <span className="text-[11px] font-black text-amber-800 dark:text-amber-300">#</span>
              <input
                type="number"
                min={1}
                max={totalItems}
                value={rankInput}
                onChange={(e) => setRankInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                onBlur={commitRank}
                className="w-8 h-5 text-center font-bold text-xs bg-white dark:bg-slate-800 rounded border border-amber-300 dark:border-amber-700 text-amber-950 dark:text-amber-200 focus:outline-none focus:ring-1 focus:ring-amber-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                title="Yeni sıra numarasını girip Enter'a basın"
              />
            </div>
            <div className="flex flex-col space-y-0.5">
              <button
                type="button"
                disabled={index === 0}
                onClick={() => onMoveUp(index)}
                className="p-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-20"
                title="Bir yukarı taşı"
              >
                <ArrowUp size={11} />
              </button>
              <button
                type="button"
                disabled={index === totalItems - 1}
                onClick={() => onMoveDown(index)}
                className="p-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-20"
                title="Bir aşağı taşı"
              >
                <ArrowDown size={11} />
              </button>
            </div>
          </div>
        ) : (
          <input
            type="checkbox"
            checked={selectedIds.has(product.id)}
            onChange={() => onSelectOne(product.id)}
            className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
          />
        )}
      </td>

      {/* 2. Image with Lightbox Preview */}
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => {
            if (product.images?.[0]) {
              onPreviewImage(product.images[0], `${product.title} Görseli`);
            }
          }}
          className="block overflow-hidden rounded-xl focus:outline-none ring-2 ring-transparent hover:ring-rose-500 transition-all group"
          title="Görseli büyütmek için tıklayın"
        >
          <img
            src={product.images?.[0] || ''}
            alt={product.title}
            className="h-12 w-12 object-cover rounded-xl border border-slate-200 dark:border-slate-700 group-hover:scale-105 transition-transform"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48"%3E%3Crect fill="%23f1f5f9" width="48" height="48"/%3E%3Ctext fill="%2394a3b8" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3E🌸%3C/text%3E%3C/svg%3E';
            }}
          />
        </button>
      </td>

      {/* 3. Title, SKU & Slug Copy */}
      <td className="px-4 py-3">
        <div className="flex flex-col space-y-0.5 max-w-[200px]">
          <p className="font-bold text-slate-900 dark:text-slate-100 truncate" title={product.title}>
            {product.title}
          </p>
          <div className="flex items-center space-x-1">
            {product.slug && (
              <button
                type="button"
                onClick={(e) => onCopySlug(e, product.slug)}
                className="inline-flex items-center space-x-1 text-[11px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                title="Ürün Linki Kopyala"
              >
                <span>/urunler/{product.slug.slice(0, 16)}...</span>
                {copiedId === product.slug ? (
                  <Check size={11} className="text-emerald-500" />
                ) : (
                  <Copy size={11} />
                )}
              </button>
            )}
          </div>
        </div>
      </td>

      {/* 4. Categories */}
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {product.categories?.slice(0, 2).map((catId, idx) => (
            <span
              key={idx}
              className="inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800"
            >
              {getCategoryName(catId)}
            </span>
          ))}
        </div>
      </td>

      {/* 5. Price - Inline Editable (Kampanya Fiyatı) */}
      <td className="px-4 py-3">
        <div className="flex flex-col space-y-1">
          <div className="text-[11px] text-slate-500 font-medium">
            Baz: <span className="font-semibold text-slate-700 dark:text-slate-300">{formatPrice(product.basePrice)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="text-[11px] font-medium text-slate-500">Kampanya:</span>
            <input
              type="number"
              min="0"
              step="0.01"
              disabled={savingProductIds[product.id] === true}
              value={priceDrafts[product.id] ?? ''}
              onChange={(e) => onPriceChange(product.id, e.target.value)}
              className="w-20 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-1 text-xs font-bold text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-rose-500"
              title="Kampanya Fiyatı"
            />
            <button
              type="button"
              onClick={() => onPriceSave(product)}
              disabled={savingProductIds[product.id] === true}
              className="p-1 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Kampanya fiyatını kaydet"
            >
              <Save size={13} />
            </button>
          </div>
          {product.campaignPrice > 0 && product.basePrice > 0 && product.campaignPrice < product.basePrice && (
            <span className="inline-flex items-center bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 px-1.5 py-0.5 rounded-md text-[10px] font-bold w-fit">
              🔥 -{product.discountPercentage}% İndirim
            </span>
          )}
        </div>
      </td>

      {/* 6. Stock */}
      <td className="px-4 py-3">
        {product.trackInventory ? (
          <div className="flex flex-col space-y-1">
            <div className="flex items-center space-x-1">
              <input
                type="number"
                min="0"
                step="1"
                disabled={savingProductIds[product.id] === true}
                value={stockDrafts[product.id] ?? ''}
                onChange={(e) => onStockChange(product.id, e.target.value)}
                className={`w-16 rounded-xl border px-2 py-1 text-xs font-bold bg-slate-50 dark:bg-slate-800 ${
                  (product.stock ?? 0) > 0 ? 'border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100' : 'border-rose-300 dark:border-rose-800 text-rose-600'
                }`}
              />
              <button
                type="button"
                onClick={() => onStockSave(product)}
                disabled={savingProductIds[product.id] === true}
                className="p-1 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                title="Stoğu kaydet"
              >
                <Save size={13} />
              </button>
            </div>
            {(product.stock ?? 0) === 0 ? (
              <span className="inline-flex items-center space-x-1 bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 px-2 py-0.5 rounded-lg text-[10px] font-bold border border-rose-200 dark:border-rose-800 w-fit">
                ⚠️ Stok Tükendi
              </span>
            ) : (
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold">
                📦 {product.stock} Adet Stokta
              </span>
            )}
          </div>
        ) : (
          <span className="text-emerald-600 dark:text-emerald-400 font-bold text-xs">♾️ Sınırsız Stok</span>
        )}
      </td>

      {/* 7. Featured Status */}
      <td className="px-4 py-3">
        <label className="inline-flex items-center space-x-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={product.featured === true}
            disabled={savingProductIds[product.id] === true}
            onChange={(e) => onFeaturedToggle(product, e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400"
          />
          <span className="font-semibold text-slate-700 dark:text-slate-300 text-xs">Öne Çıkar</span>
        </label>
      </td>

      {/* 8. Status Toggle */}
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => onStatusToggle(product)}
          disabled={savingProductIds[product.id] === true}
          className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all ${
            product.status === 'active'
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200'
              : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200'
          }`}
        >
          {product.status === 'active' ? '✅ Aktif' : '❌ Pasif'}
        </button>
      </td>

      {/* 9. Actions */}
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end space-x-1">
          <button
            type="button"
            onClick={() => onNavigateEdit(product.id)}
            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/50 rounded-lg transition-colors"
            title="Ürünü Düzenle"
          >
            <Edit2 size={15} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(product.id, product.title)}
            className="p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-lg transition-colors"
            title="Ürünü Sil"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </td>
    </tr>
  );
};

const SortableProductCard: React.FC<SortableProductRowProps> = ({
  product,
  index,
  totalItems,
  isFeaturedMode,
  selectedIds,
  savingProductIds,
  priceDrafts,
  stockDrafts,
  getCategoryName,
  onSelectOne,
  onPreviewImage,
  onPriceChange,
  onPriceSave,
  onStockChange,
  onStockSave,
  onFeaturedToggle,
  onStatusToggle,
  onDelete,
  onNavigateEdit,
  onMoveUp,
  onMoveDown,
  onMoveToRank,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id, disabled: !isFeaturedMode });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    position: 'relative',
    zIndex: isDragging ? 50 : undefined,
  };

  const [rankInput, setRankInput] = useState(String(index + 1));
  useEffect(() => {
    setRankInput(String(index + 1));
  }, [index]);

  const commitRank = () => {
    const parsed = parseInt(rankInput, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= totalItems && parsed !== index + 1) {
      onMoveToRank(index, parsed);
    } else {
      setRankInput(String(index + 1));
    }
  };

  const isSaving = savingProductIds[product.id] || false;
  const isSelected = selectedIds.has(product.id);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex flex-col bg-white dark:bg-slate-800 rounded-2xl border transition-all duration-200 overflow-hidden shadow-xs ${
        isDragging
          ? 'border-amber-400 ring-2 ring-amber-400 shadow-xl'
          : isSelected
          ? 'border-rose-400 ring-2 ring-rose-200 dark:ring-rose-900/50 shadow-sm'
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md'
      }`}
    >
      {/* Top action bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-100 dark:border-slate-700/60">
        {isFeaturedMode ? (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-1.5">
              <button
                type="button"
                className="p-1 text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 cursor-grab active:cursor-grabbing rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                title="Sürükleyip sıralamayı değiştirin"
                {...attributes}
                {...listeners}
              >
                <GripVertical size={16} />
              </button>
              <div className="flex items-center space-x-1 bg-amber-50 dark:bg-amber-950/60 px-1.5 py-0.5 rounded-lg border border-amber-300/80 shadow-xs">
                <span className="text-[11px] font-black text-amber-800 dark:text-amber-300">#</span>
                <input
                  type="number"
                  min={1}
                  max={totalItems}
                  value={rankInput}
                  onChange={(e) => setRankInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  }}
                  onBlur={commitRank}
                  className="w-8 h-5 text-center font-bold text-xs bg-white dark:bg-slate-800 rounded border border-amber-300 dark:border-amber-700 text-amber-950 dark:text-amber-200 focus:outline-none focus:ring-1 focus:ring-amber-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  title="Yeni sıra numarasını girip Enter'a basın"
                />
              </div>
            </div>
            <div className="flex items-center space-x-0.5">
              <button
                type="button"
                disabled={index === 0}
                onClick={() => onMoveUp(index)}
                className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-20 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
                title="Bir yukarı taşı"
              >
                <ArrowUp size={13} />
              </button>
              <button
                type="button"
                disabled={index === totalItems - 1}
                onClick={() => onMoveDown(index)}
                className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-20 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
                title="Bir aşağı taşı"
              >
                <ArrowDown size={13} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between w-full">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelectOne(product.id)}
              className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
            />
            <span className="text-[11px] text-slate-400 font-mono">#{index + 1}</span>
          </div>
        )}
      </div>

      {/* Product Image */}
      <div className="relative aspect-square w-full bg-slate-100 dark:bg-slate-900 overflow-hidden group/img">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.title}
            className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <Package size={36} />
          </div>
        )}
        <button
          type="button"
          onClick={() => product.images?.[0] && onPreviewImage(product.images[0], `${product.title} Görseli`)}
          className="absolute inset-0 bg-black/0 group-hover/img:bg-black/35 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-all text-white"
          title="Büyütmek için tıklayın"
        >
          <Eye size={24} className="drop-shadow-md" />
        </button>

        {/* Floating Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1 pointer-events-none">
          {product.featured && (
            <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-500/90 text-white backdrop-blur-xs shadow-xs">
              <Star size={10} className="fill-white" />
              <span>Öne Çıkan</span>
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2 pointer-events-none">
          <span
            className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold backdrop-blur-xs shadow-xs ${
              product.status === 'active'
                ? 'bg-emerald-500/90 text-white'
                : 'bg-slate-600/90 text-white'
            }`}
          >
            {product.status === 'active' ? 'Aktif' : 'Pasif'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 flex-1 flex flex-col justify-between space-y-2">
        <div>
          <p className="text-[11px] text-slate-400 font-medium truncate">
            {product.categories?.[0] ? getCategoryName(product.categories[0]) : 'Genel'}
          </p>
          <h4
            onClick={() => onNavigateEdit(product.id)}
            className="text-xs font-semibold text-slate-900 dark:text-white line-clamp-2 hover:text-rose-600 dark:hover:text-rose-400 cursor-pointer transition-colors mt-0.5"
            title={product.title}
          >
            {product.title}
          </h4>
        </div>

        {/* Price & Stock */}
        <div className="pt-2 border-t border-slate-100 dark:border-slate-700/60 flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-[10px] text-slate-400 font-medium">
                Baz: {product.campaignPrice > 0 && product.campaignPrice < product.basePrice ? (
                  <span className="line-through">{formatPrice(product.basePrice)}</span>
                ) : (
                  <span className="font-semibold text-slate-700 dark:text-slate-300">{formatPrice(product.basePrice)}</span>
                )}
              </span>
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                Kampanya: {product.campaignPrice > 0 ? formatPrice(product.campaignPrice) : 'Yok'}
              </span>
            </div>
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                product.stock <= 0
                  ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
              }`}
            >
              Stok: {product.stock}
            </span>
          </div>

          {/* Quick Edit Kampanya Fiyatı in Grid Mode */}
          <div className="flex items-center justify-between pt-1.5 border-t border-slate-100 dark:border-slate-700/40">
            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">Hızlı Kampanya:</span>
            <div className="flex items-center space-x-1">
              <input
                type="number"
                min="0"
                step="0.01"
                disabled={isSaving}
                value={priceDrafts[product.id] ?? ''}
                onChange={(e) => onPriceChange(product.id, e.target.value)}
                className="w-16 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-1.5 py-0.5 text-xs font-bold text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-rose-500"
                placeholder="Kampanya"
              />
              <button
                type="button"
                onClick={() => onPriceSave(product)}
                disabled={isSaving}
                className="p-1 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                title="Kampanya fiyatını kaydet"
              >
                <Save size={12} />
              </button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="pt-2 flex items-center justify-between border-t border-slate-100 dark:border-slate-700/60">
          <button
            type="button"
            onClick={() => onStatusToggle(product)}
            disabled={isSaving}
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-lg transition-colors ${
              product.status === 'active'
                ? 'text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/50'
                : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
            }`}
          >
            {product.status === 'active' ? '● Aktif' : '○ Pasif'}
          </button>
          <div className="flex items-center space-x-1">
            <button
              type="button"
              onClick={() => onNavigateEdit(product.id)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/50 rounded-lg transition-colors"
              title="Ürünü Düzenle"
            >
              <Edit2 size={13} />
            </button>
            <button
              type="button"
              onClick={() => onDelete(product.id, product.title)}
              className="p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-lg transition-colors"
              title="Ürünü Sil"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Products: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<ProductStats | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingProductIds, setSavingProductIds] = useState<Record<string, boolean>>({});
  const [featuredOrderDrafts, setFeaturedOrderDrafts] = useState<Record<string, string>>({});
  const [stockDrafts, setStockDrafts] = useState<Record<string, string>>({});
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const [previewImage, setPreviewImage] = useState<{ url: string; title: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Sürükle-Bırak Sıralama Durumu
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  // Görünüm Modu: Liste vs Grid
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    return (localStorage.getItem('products_view_mode') as 'list' | 'grid') || 'list';
  });

  const handleViewModeChange = (mode: 'list' | 'grid') => {
    setViewMode(mode);
    localStorage.setItem('products_view_mode', mode);
  };

  // Filtreler — server-side
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [outOfStockOnly, setOutOfStockOnly] = useState(false);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(20);

  // Toplu seçim
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActing, setBulkActing] = useState(false);

  // DnD Sensors (PointerSensor distance 5 avoids accidental drags on clicks)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Arama kutusuna debounce
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Filtre değişince ilk sayfaya dön
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterCategories, minPrice, maxPrice, outOfStockOnly, featuredOnly, sortBy, sortDir, pageSize]);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      const data = await productApi.getAllProducts({
        search: searchTerm.trim() || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        category: filterCategories.length > 0 ? filterCategories.join(',') : undefined,
        minPrice: minPrice.trim() ? Number(minPrice) : undefined,
        maxPrice: maxPrice.trim() ? Number(maxPrice) : undefined,
        outOfStock: outOfStockOnly || undefined,
        featured: featuredOnly || undefined,
        sortBy: featuredOnly ? 'featuredOrder' : sortBy,
        sortDir: featuredOnly ? 'asc' : sortDir,
        limit: featuredOnly ? 100 : pageSize,
        offset: featuredOnly ? 0 : offset,
      });

      setProducts(data.products);
      setTotal(data.total);
      setHasOrderChanges(false);

      setFeaturedOrderDrafts(
        data.products.reduce<Record<string, string>>((drafts, product) => {
          drafts[product.id] = product.featuredOrder && product.featuredOrder > 0
            ? String(product.featuredOrder)
            : '';
          return drafts;
        }, {})
      );
      setStockDrafts(
        data.products.reduce<Record<string, string>>((drafts, product) => {
          drafts[product.id] = String(product.stock ?? 0);
          return drafts;
        }, {})
      );
      setPriceDrafts(
        data.products.reduce<Record<string, string>>((drafts, product) => {
          drafts[product.id] = String(product.campaignPrice ?? 0);
          return drafts;
        }, {})
      );
      setSelectedIds((prev) => {
        const validIds = new Set(data.products.map((p) => p.id));
        const next = new Set<string>();
        prev.forEach((id) => { if (validIds.has(id)) next.add(id); });
        return next;
      });
    } catch (error: any) {
      console.error('Ürünler yüklenirken hata:', error);
      toast.error(error.message || 'Ürünler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStatus, filterCategories, minPrice, maxPrice, outOfStockOnly, featuredOnly, sortBy, sortDir, currentPage, pageSize]);

  const loadStats = useCallback(async () => {
    try {
      const data = await productApi.getStats();
      setStats(data);
    } catch (error) {
      console.warn('İstatistikler yüklenemedi:', error);
    }
  }, []);

  const loadCategories = async () => {
    try {
      const data = await categoryApi.getCategories();
      setCategories(data);
    } catch (error) {
      console.warn('Kategoriler yüklenemedi:', error);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadStats();
    loadCategories();
  }, [loadStats]);

  // Drag and Drop Handler
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setProducts((prev) => {
        const oldIndex = prev.findIndex((p) => p.id === active.id);
        const newIndex = prev.findIndex((p) => p.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          setHasOrderChanges(true);
          return arrayMove(prev, oldIndex, newIndex);
        }
        return prev;
      });
    }
  };

  // Nudge Move Handlers
  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    setProducts((prev) => {
      setHasOrderChanges(true);
      return arrayMove(prev, index, index - 1);
    });
  };

  const handleMoveDown = (index: number) => {
    setProducts((prev) => {
      if (index >= prev.length - 1) return prev;
      setHasOrderChanges(true);
      return arrayMove(prev, index, index + 1);
    });
  };

  const handleMoveToRank = (fromIndex: number, targetRank: number) => {
    const targetIndex = Math.max(0, Math.min(products.length - 1, targetRank - 1));
    if (fromIndex === targetIndex) return;
    setProducts((prev) => {
      setHasOrderChanges(true);
      return arrayMove(prev, fromIndex, targetIndex);
    });
  };

  // Save Bulk Reorder
  const handleSaveOrder = async () => {
    if (products.length === 0) return;
    setSavingOrder(true);
    try {
      const orderedIds = products.map((p) => p.id);
      await productApi.reorderFeaturedProducts(orderedIds);
      toast.success('✅ Öne çıkan ürün sıralaması kaydedildi! Web ana sayfası güncellendi.');
      setHasOrderChanges(false);
      loadProducts();
    } catch (error: any) {
      toast.error(error.message || 'Sıralama kaydedilemedi');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleCancelReorder = () => {
    setHasOrderChanges(false);
    loadProducts();
  };

  const handleDelete = async (id: string, title: string) => {
    if (window.confirm(`"${title}" ürününü silmek istediğinize emin misiniz?`)) {
      try {
        await productApi.deleteProduct(id);
        toast.success('✅ Ürün silindi!');
        loadProducts();
        loadStats();
      } catch (error: any) {
        console.error('Ürün silinirken hata:', error);
        toast.error(error.message || 'Ürün silinemedi');
      }
    }
  };

  const handleCopySlug = (e: React.MouseEvent, slug: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`/urunler/${slug}`);
    setCopiedId(slug);
    toast.success(`Ürün linki kopyalandı: /urunler/${slug}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleFeaturedToggle = async (product: Product, featured: boolean) => {
    const nextDraft = featured ? (featuredOrderDrafts[product.id] ?? '') : '';

    setSavingProductIds((prev) => ({ ...prev, [product.id]: true }));

    try {
      const updatedProduct = await productApi.updateProduct(product.id, {
        featured,
        featuredOrder: featured && nextDraft.trim() ? Number(nextDraft) : undefined,
      });

      setProducts((prev) => prev.map((item) => item.id === product.id ? updatedProduct : item));
      setFeaturedOrderDrafts((prev) => ({
        ...prev,
        [product.id]: updatedProduct.featuredOrder && updatedProduct.featuredOrder > 0
          ? String(updatedProduct.featuredOrder)
          : '',
      }));
      toast.success(featured ? 'Ürün öne çıkarıldı' : 'Ürün öne çıkarma listesinden çıkarıldı');
      loadStats();
    } catch (error: any) {
      toast.error(error.message || 'Öne çıkarma durumu güncellenemedi');
    } finally {
      setSavingProductIds((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const handleStatusToggle = async (product: Product) => {
    const nextStatus = product.status === 'active' ? 'inactive' : 'active';
    setSavingProductIds((prev) => ({ ...prev, [product.id]: true }));
    try {
      const updatedProduct = await productApi.updateStatus(product.id, nextStatus);
      setProducts((prev) => prev.map((item) => item.id === product.id ? updatedProduct : item));
      toast.success(nextStatus === 'active' ? 'Ürün aktif edildi' : 'Ürün pasife alındı');
      loadStats();
    } catch (error: any) {
      toast.error(error.message || 'Durum güncellenemedi');
    } finally {
      setSavingProductIds((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const handleStockSave = async (product: Product) => {
    const rawValue = stockDrafts[product.id] ?? '';
    const stockNum = Number(rawValue);

    if (rawValue.trim() === '' || Number.isNaN(stockNum) || stockNum < 0) {
      toast.error('Geçerli bir stok adedi girin (0 veya daha büyük)');
      return;
    }

    setSavingProductIds((prev) => ({ ...prev, [product.id]: true }));
    try {
      const updatedProduct = await productApi.updateProduct(product.id, { stock: stockNum });
      setProducts((prev) => prev.map((item) => item.id === product.id ? updatedProduct : item));
      setStockDrafts((prev) => ({ ...prev, [product.id]: String(updatedProduct.stock ?? 0) }));
      toast.success('Stok güncellendi');
    } catch (error: any) {
      toast.error(error.message || 'Stok güncellenemedi');
    } finally {
      setSavingProductIds((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const handlePriceSave = async (product: Product) => {
    const rawValue = priceDrafts[product.id] ?? '';
    const priceNum = Number(rawValue);

    if (rawValue.trim() === '' || Number.isNaN(priceNum) || priceNum < 0) {
      toast.error('Geçerli bir fiyat girin (0 veya daha büyük)');
      return;
    }

    setSavingProductIds((prev) => ({ ...prev, [product.id]: true }));
    try {
      const updatedProduct = await productApi.updateProduct(product.id, { campaignPrice: priceNum });
      setProducts((prev) => prev.map((item) => item.id === product.id ? updatedProduct : item));
      setPriceDrafts((prev) => ({ ...prev, [product.id]: String(updatedProduct.campaignPrice ?? 0) }));
      toast.success('Kampanya fiyatı güncellendi');
    } catch (error: any) {
      toast.error(error.message || 'Kampanya fiyatı güncellenemedi');
    } finally {
      setSavingProductIds((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const allSelected = products.length > 0 && products.every((p) => prev.has(p.id));
      if (allSelected) return new Set();
      return new Set(products.map((p) => p.id));
    });
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} ürünü silmek istediğinize emin misiniz?`)) return;

    setBulkActing(true);
    try {
      await Promise.all(Array.from(selectedIds).map((id) => productApi.deleteProduct(id)));
      toast.success('Seçili ürünler silindi');
      setSelectedIds(new Set());
      loadProducts();
      loadStats();
    } catch (error: any) {
      toast.error(error.message || 'Toplu silme başarısız');
    } finally {
      setBulkActing(false);
    }
  };

  const handleBulkStatus = async (status: 'active' | 'inactive') => {
    if (selectedIds.size === 0) return;
    const label = status === 'active' ? 'aktif' : 'pasif';
    if (!window.confirm(`${selectedIds.size} ürünü ${label} yapmak istediğinize emin misiniz?`)) return;

    setBulkActing(true);
    try {
      const result = await productApi.bulkUpdateStatus(Array.from(selectedIds), status);
      toast.success(result.message);
      setSelectedIds(new Set());
      loadProducts();
      loadStats();
    } catch (error: any) {
      toast.error(error.message || 'Toplu durum güncelleme başarısız');
    } finally {
      setBulkActing(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir(field === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={14} className="text-gray-400" />;
    return sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const allOnPageSelected = products.length > 0 && products.every((p) => selectedIds.has(p.id));

  const toggleCategoryFilter = (categoryId: string) => {
    setFilterCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const statusTabItems = [
    { id: 'all', label: 'Tüm Ürünler', count: stats?.total, active: filterStatus === 'all' && !outOfStockOnly && !featuredOnly },
    { id: 'active', label: '✅ Aktif Ürünler', count: stats?.active, active: filterStatus === 'active' && !outOfStockOnly && !featuredOnly },
    { id: 'inactive', label: '❌ Pasif Ürünler', count: (stats?.total ?? 0) - (stats?.active ?? 0), active: filterStatus === 'inactive' && !outOfStockOnly && !featuredOnly },
    { id: 'outOfStock', label: '📦 Stokta Yok', active: outOfStockOnly },
    { id: 'featured', label: '⭐ Öne Çıkanlar (Sıralama)', count: stats?.featured, active: featuredOnly },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">
            Ürün Yönetimi
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {featuredOnly ? (
              <span>
                Toplam <span className="font-semibold text-amber-600 dark:text-amber-400">{products.length}</span> öne çıkan ürün vitrin sıralamasında gösteriliyor
              </span>
            ) : (
              <span>
                Katalogda toplam <span className="font-semibold text-slate-700 dark:text-slate-300">{total}</span> ürün listeleniyor
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={() => navigate('/admin/products/bulk-price')}
            className="btn btn-secondary"
          >
            <DollarSign size={16} />
            <span>Toplu Fiyat</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/products/new')}
            className="btn btn-primary"
          >
            <Plus size={18} />
            <span>Yeni Ürün Ekle</span>
          </button>
        </div>
      </div>

      {/* Sticky Save Order Banner (Appears when user drags or nudges featured items) */}
      {hasOrderChanges && featuredOnly && (
        <div className="sticky top-3 z-30 bg-gradient-to-r from-amber-600 via-amber-500 to-rose-600 text-white px-5 py-3.5 rounded-2xl shadow-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-amber-300/50 backdrop-blur-md animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Star className="text-white fill-white" size={18} />
            </div>
            <div>
              <p className="font-bold text-sm tracking-wide">Öne Çıkan Sıralaması Değiştirildi</p>
              <p className="text-xs text-amber-100">Yeni vitrin sırasının web ana sayfasında yayınlanması için kaydedin.</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 self-end sm:self-auto">
            <button
              type="button"
              onClick={handleCancelReorder}
              disabled={savingOrder}
              className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-white/20 hover:bg-white/30 text-white transition-colors"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleSaveOrder}
              disabled={savingOrder}
              className="px-4 py-2 rounded-xl text-xs font-black bg-white text-slate-900 hover:bg-amber-50 shadow-lg transition-all flex items-center space-x-2 active:scale-95"
            >
              {savingOrder ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-900 border-t-transparent" />
              ) : (
                <Save size={15} className="text-emerald-600" />
              )}
              <span>Sıralamayı Kaydet</span>
            </button>
          </div>
        </div>
      )}

      {/* Status Filter Tabs (Compact Metric Bar) */}
      <div className="flex items-center space-x-1.5 overflow-x-auto pb-1.5 pt-0.5 no-scrollbar border-b border-slate-200 dark:border-slate-800">
        {statusTabItems.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              if (hasOrderChanges && !window.confirm('Kaydedilmemiş sıralama değişiklikleri var. Çıkmak istediğinize emin misiniz?')) {
                return;
              }
              setHasOrderChanges(false);
              if (tab.id === 'all') {
                setFilterStatus('all');
                setOutOfStockOnly(false);
                setFeaturedOnly(false);
              } else if (tab.id === 'active') {
                setFilterStatus('active');
                setOutOfStockOnly(false);
                setFeaturedOnly(false);
              } else if (tab.id === 'inactive') {
                setFilterStatus('inactive');
                setOutOfStockOnly(false);
                setFeaturedOnly(false);
              } else if (tab.id === 'outOfStock') {
                setOutOfStockOnly(true);
                setFeaturedOnly(false);
              } else if (tab.id === 'featured') {
                setFeaturedOnly(true);
                setOutOfStockOnly(false);
                setFilterStatus('all');
              }
            }}
            className={`flex items-center space-x-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
              tab.active
                ? tab.id === 'featured'
                  ? 'bg-gradient-to-r from-amber-500 to-rose-600 text-white shadow-md scale-[1.02]'
                  : 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-md scale-[1.02]'
                : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800'
            }`}
          >
            <span>{tab.label}</span>
            {typeof tab.count === 'number' && (
              <span
                className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                  tab.active
                    ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900'
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Featured Reorder Hint */}
      {featuredOnly && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-800/60 rounded-2xl p-3.5 flex items-center justify-between gap-3 text-xs text-amber-900 dark:text-amber-200">
          <div className="flex items-center space-x-2.5">
            <span className="text-base">💡</span>
            <span>
              <strong>Sürükle-Bırak Sıralama:</strong> Satır başındaki tutamaçtan tutup yukarı/aşağı sürükleyerek veya oklarla taşıyarak ana sayfadaki vitrin sırasını belirleyebilirsiniz. Sıralama sonrasında yukarıdaki <strong>"Sıralamayı Kaydet"</strong> butonuna basın.
            </span>
          </div>
          {hasOrderChanges && (
            <span className="px-2.5 py-1 rounded-full text-[11px] font-black bg-rose-500 text-white animate-pulse whitespace-nowrap">
              Kaydedilmemiş değişiklikler var!
            </span>
          )}
        </div>
      )}

      {/* Filters Card */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 space-y-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Main Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Ürün adı, kategori veya SKU ara..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs pl-9 pr-8 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
                title="Aramayı temizle"
              >
                ✕
              </button>
            )}
          </div>

          {/* Quick Refresh */}
          <button
            type="button"
            onClick={() => loadProducts()}
            disabled={loading}
            className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center space-x-1 text-xs font-semibold"
            title="Listeyi yenile"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Yenile</span>
          </button>

          {/* View Mode Toggle: Liste vs Grid */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800/90 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={() => handleViewModeChange('list')}
              className={`flex items-center space-x-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Liste Görünümü"
            >
              <ListIcon size={14} />
              <span className="hidden sm:inline">Liste</span>
            </button>
            <button
              type="button"
              onClick={() => handleViewModeChange('grid')}
              className={`flex items-center space-x-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Grid (Kart) Görünümü"
            >
              <LayoutGrid size={14} />
              <span className="hidden sm:inline">Grid</span>
            </button>
          </div>
        </div>

        {/* Categories Chips */}
        {categories.length > 0 && !featuredOnly && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {categories.map((c) => {
              const active = filterCategories.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCategoryFilter(c.id)}
                  className={`px-2.5 py-1 rounded-xl text-[11px] font-semibold transition-colors ${
                    active
                      ? 'bg-rose-500 text-white shadow-xs'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Bulk Actions Bar (Normal Mode) */}
      {selectedIds.size > 0 && !featuredOnly && (
        <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 p-3 rounded-2xl flex items-center justify-between text-xs animate-in fade-in duration-200">
          <span className="font-bold text-rose-900 dark:text-rose-200">
            {selectedIds.size} ürün seçildi
          </span>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => handleBulkStatus('active')}
              disabled={bulkActing}
              className="px-3 py-1.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition"
            >
              Aktif Yap
            </button>
            <button
              type="button"
              onClick={() => handleBulkStatus('inactive')}
              disabled={bulkActing}
              className="px-3 py-1.5 rounded-xl bg-slate-700 text-white font-bold hover:bg-slate-800 transition"
            >
              Pasif Yap
            </button>
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={bulkActing}
              className="px-3 py-1.5 rounded-xl bg-rose-600 text-white font-bold hover:bg-rose-700 transition"
            >
              Sil
            </button>
          </div>
        </div>
      )}

      {/* Products Table or Grid Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        {featuredOnly ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={products.map((p) => p.id)}
              strategy={viewMode === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}
            >
              {viewMode === 'list' ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-slate-700 dark:text-slate-300 text-left">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 font-bold border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="px-4 py-3 w-36">
                          <span className="text-amber-600 dark:text-amber-400 font-black">Sıra / Taşı</span>
                        </th>
                        <th className="px-4 py-3 text-left">Görsel</th>
                        <th className="px-4 py-3 text-left">Ürün / Slug</th>
                        <th className="px-4 py-3 text-left">Kategori</th>
                        <th className="px-4 py-3 text-left">Fiyat</th>
                        <th className="px-4 py-3 text-left">Stok</th>
                        <th className="px-4 py-3 text-left">Anasayfa</th>
                        <th className="px-4 py-3 text-left">Durum</th>
                        <th className="px-4 py-3 text-right">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {loading ? (
                        <tr>
                          <td colSpan={9} className="py-12 text-center text-slate-400">
                            <div className="flex flex-col items-center justify-center space-y-2">
                              <div className="animate-spin rounded-full h-7 w-7 border-2 border-rose-500 border-t-transparent" />
                              <span>Öne çıkan ürünler yükleniyor...</span>
                            </div>
                          </td>
                        </tr>
                      ) : products.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-12 text-center text-slate-400">
                            <p className="text-sm font-semibold">Öne çıkarılmış ürün bulunamadı</p>
                            <p className="text-xs text-slate-400 mt-1">
                              "Tüm Ürünler" sekmesinden istediğiniz ürünlerin "Öne Çıkar" kutucuğunu işaretleyin.
                            </p>
                          </td>
                        </tr>
                      ) : (
                        products.map((product, idx) => (
                          <SortableProductRow
                            key={product.id}
                            product={product}
                            index={idx}
                            totalItems={products.length}
                            isFeaturedMode={true}
                            selectedIds={selectedIds}
                            savingProductIds={savingProductIds}
                            priceDrafts={priceDrafts}
                            stockDrafts={stockDrafts}
                            copiedId={copiedId}
                            getCategoryName={getCategoryName}
                            onSelectOne={toggleSelectOne}
                            onPreviewImage={(url, title) => setPreviewImage({ url, title })}
                            onCopySlug={handleCopySlug}
                            onPriceChange={(id, val) => setPriceDrafts((prev) => ({ ...prev, [id]: val }))}
                            onPriceSave={handlePriceSave}
                            onStockChange={(id, val) => setStockDrafts((prev) => ({ ...prev, [id]: val }))}
                            onStockSave={handleStockSave}
                            onFeaturedToggle={handleFeaturedToggle}
                            onStatusToggle={handleStatusToggle}
                            onDelete={handleDelete}
                            onNavigateEdit={(id) => navigate(`/admin/products/${id}/edit`)}
                            onMoveUp={handleMoveUp}
                            onMoveDown={handleMoveDown}
                            onMoveToRank={handleMoveToRank}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Grid View (Featured with DnD & Rank input) */
                <div className="p-4">
                  {loading ? (
                    <div className="py-12 text-center text-slate-400">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-rose-500 border-t-transparent" />
                        <span>Öne çıkan ürünler yükleniyor...</span>
                      </div>
                    </div>
                  ) : products.length === 0 ? (
                    <div className="py-12 text-center text-slate-400">
                      <p className="text-sm font-semibold">Öne çıkarılmış ürün bulunamadı</p>
                      <p className="text-xs text-slate-400 mt-1">
                        "Tüm Ürünler" sekmesinden istediğiniz ürünlerin "Öne Çıkar" kutucuğunu işaretleyin.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                      {products.map((product, idx) => (
                        <SortableProductCard
                          key={product.id}
                          product={product}
                          index={idx}
                          totalItems={products.length}
                          isFeaturedMode={true}
                          selectedIds={selectedIds}
                          savingProductIds={savingProductIds}
                          priceDrafts={priceDrafts}
                          stockDrafts={stockDrafts}
                          copiedId={copiedId}
                          getCategoryName={getCategoryName}
                          onSelectOne={toggleSelectOne}
                          onPreviewImage={(url, title) => setPreviewImage({ url, title })}
                          onCopySlug={handleCopySlug}
                          onPriceChange={(id, val) => setPriceDrafts((prev) => ({ ...prev, [id]: val }))}
                          onPriceSave={handlePriceSave}
                          onStockChange={(id, val) => setStockDrafts((prev) => ({ ...prev, [id]: val }))}
                          onStockSave={handleStockSave}
                          onFeaturedToggle={handleFeaturedToggle}
                          onStatusToggle={handleStatusToggle}
                          onDelete={handleDelete}
                          onNavigateEdit={(id) => navigate(`/admin/products/${id}/edit`)}
                          onMoveUp={handleMoveUp}
                          onMoveDown={handleMoveDown}
                          onMoveToRank={handleMoveToRank}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </SortableContext>
          </DndContext>
        ) : (
          viewMode === 'list' ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-slate-700 dark:text-slate-300 text-left">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 font-bold border-b border-slate-200 dark:border-slate-800">
                  <tr>
                    <th className="px-4 py-3 w-28">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleSelectAllOnPage}
                        className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
                        title="Bu sayfadaki tüm ürünleri seç"
                      />
                    </th>
                    <th className="px-4 py-3 text-left">Görsel</th>
                    <th className="px-4 py-3 text-left">Ürün / Slug</th>
                    <th className="px-4 py-3 text-left">Kategori</th>
                    <th className="px-4 py-3 text-left">
                      <button type="button" onClick={() => handleSort('basePrice')} className="inline-flex items-center gap-1 hover:text-rose-600">
                        Fiyat <SortIcon field="basePrice" />
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <button type="button" onClick={() => handleSort('stock')} className="inline-flex items-center gap-1 hover:text-rose-600">
                        Stok <SortIcon field="stock" />
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left">Anasayfa</th>
                    <th className="px-4 py-3 text-left">Durum</th>
                    <th className="px-4 py-3 text-right">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-slate-400">
                        <div className="flex flex-col items-center justify-center space-y-2">
                          <div className="animate-spin rounded-full h-7 w-7 border-2 border-rose-500 border-t-transparent" />
                          <span>Ürünler yükleniyor...</span>
                        </div>
                      </td>
                    </tr>
                  ) : products.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-slate-400">
                        <p className="text-sm font-semibold">Arama kriterlerine uygun ürün bulunamadı</p>
                      </td>
                    </tr>
                  ) : (
                    products.map((product, idx) => (
                      <SortableProductRow
                        key={product.id}
                        product={product}
                        index={idx}
                        totalItems={products.length}
                        isFeaturedMode={false}
                        selectedIds={selectedIds}
                        savingProductIds={savingProductIds}
                        priceDrafts={priceDrafts}
                        stockDrafts={stockDrafts}
                        copiedId={copiedId}
                        getCategoryName={getCategoryName}
                        onSelectOne={toggleSelectOne}
                        onPreviewImage={(url, title) => setPreviewImage({ url, title })}
                        onCopySlug={handleCopySlug}
                        onPriceChange={(id, val) => setPriceDrafts((prev) => ({ ...prev, [id]: val }))}
                        onPriceSave={handlePriceSave}
                        onStockChange={(id, val) => setStockDrafts((prev) => ({ ...prev, [id]: val }))}
                        onStockSave={handleStockSave}
                        onFeaturedToggle={handleFeaturedToggle}
                        onStatusToggle={handleStatusToggle}
                        onDelete={handleDelete}
                        onNavigateEdit={(id) => navigate(`/admin/products/${id}/edit`)}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                        onMoveToRank={handleMoveToRank}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            /* Grid View (Regular Non-Featured) */
            <div className="p-4">
              {loading ? (
                <div className="py-12 text-center text-slate-400">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-rose-500 border-t-transparent" />
                    <span>Ürünler yükleniyor...</span>
                  </div>
                </div>
              ) : products.length === 0 ? (
                <div className="py-12 text-center text-slate-400">
                  <p className="text-sm font-semibold">Arama kriterlerine uygun ürün bulunamadı</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {products.map((product, idx) => (
                    <SortableProductCard
                      key={product.id}
                      product={product}
                      index={idx}
                      totalItems={products.length}
                      isFeaturedMode={false}
                      selectedIds={selectedIds}
                      savingProductIds={savingProductIds}
                      priceDrafts={priceDrafts}
                      stockDrafts={stockDrafts}
                      copiedId={copiedId}
                      getCategoryName={getCategoryName}
                      onSelectOne={toggleSelectOne}
                      onPreviewImage={(url, title) => setPreviewImage({ url, title })}
                      onCopySlug={handleCopySlug}
                      onPriceChange={(id, val) => setPriceDrafts((prev) => ({ ...prev, [id]: val }))}
                      onPriceSave={handlePriceSave}
                      onStockChange={(id, val) => setStockDrafts((prev) => ({ ...prev, [id]: val }))}
                      onStockSave={handleStockSave}
                      onFeaturedToggle={handleFeaturedToggle}
                      onStatusToggle={handleStatusToggle}
                      onDelete={handleDelete}
                      onNavigateEdit={(id) => navigate(`/admin/products/${id}/edit`)}
                      onMoveUp={handleMoveUp}
                      onMoveDown={handleMoveDown}
                      onMoveToRank={handleMoveToRank}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        )}

        {/* Pagination / Summary Bar */}
        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {featuredOnly ? (
            <div className="flex items-center space-x-2 text-xs text-slate-500">
              <span>⭐ Toplam <strong className="text-slate-800 dark:text-slate-200">{products.length}</strong> öne çıkan ürün listeleniyor (Tümü tek sayfada sıralanabilir)</span>
            </div>
          ) : (
            <>
              <div className="flex items-center space-x-2 text-xs text-slate-500">
                <span>Sayfa Başına:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-xs font-semibold text-slate-800 dark:text-slate-200"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-xs text-slate-500 font-medium mr-2">
                  Sayfa <span className="font-bold text-slate-800 dark:text-slate-200">{safeCurrentPage}</span> / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-40"
                >
                  <ChevronsLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-40"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-40"
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-40"
                >
                  <ChevronsRight size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {previewImage && (
        <ImagePreviewModal
          isOpen={true}
          imageUrl={previewImage.url}
          title={previewImage.title}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
};
